import React from "react";
import { useTheme } from "../themes/ThemeProvider";
import { Icon, CheckMark } from "./checkout/icons";

/**
 * Checkout progress indicator.
 *
 * Icons + labels are whitelabel-driven via `branding.checkout`:
 *   - `stepIcons`  — icon name per step key, from the SVG registry in
 *                    `./checkout/icons.tsx`. Falls back to the per-step
 *                    default below when a store doesn't override.
 *   - `stepLabels` — optional label override per step key.
 *   - `steps`      — ordered list of step keys. When absent, we use the
 *                    previous hardcoded order `[shipping, payment, review]`
 *                    so existing scenarios keep working.
 *   - `styling.progressIndicator` — `"steps"` (default), `"progress-bar"`,
 *                    or `"none"`. This component only renders when the
 *                    value is `"steps"`; the parent decides layout for
 *                    the other two.
 *
 * No emoji is ever rendered — the previous `📦 💳 ✅` produced an
 * instant "AI-demo" visual signal on production; SVG line icons match
 * the editorial / minimal / enterprise aesthetics of each store.
 */

interface CheckoutStepsProps {
  currentStep: string;
}

type StepSpec = { key: string; label: string; icon: string };

const DEFAULT_STEPS: StepSpec[] = [
  { key: "shipping", label: "Shipping", icon: "package" },
  { key: "payment", label: "Payment", icon: "credit-card" },
  { key: "review", label: "Review", icon: "shield-check" },
];

const DEFAULT_STEP_LABEL: Record<string, string> = {
  information: "Information",
  contact: "Contact",
  shipping: "Shipping",
  delivery: "Delivery",
  payment: "Payment",
  review: "Review",
  confirmation: "Confirmation",
  cart: "Cart",
};

const DEFAULT_STEP_ICON: Record<string, string> = {
  information: "user",
  contact: "user",
  shipping: "package",
  delivery: "truck",
  payment: "credit-card",
  review: "shield-check",
  confirmation: "check-circle",
  cart: "package",
};

function titleCase(key: string): string {
  return key
    .split(/[-_\s]/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

const CheckoutSteps: React.FC<CheckoutStepsProps> = ({ currentStep }) => {
  const { branding } = useTheme();
  const checkoutConfig = branding?.checkout;

  // Honour "none" / "progress-bar" — not rendered by this component.
  if (checkoutConfig?.styling?.progressIndicator === "none") return null;

  // Build the step list from branding if provided, otherwise fall back
  // to the legacy hardcoded order so no existing integration-test breaks.
  const stepKeys = checkoutConfig?.steps?.length
    ? checkoutConfig.steps
    : DEFAULT_STEPS.map((s) => s.key);

  const labelOverrides = checkoutConfig?.stepLabels || {};
  const iconOverrides = checkoutConfig?.stepIcons || {};

  const steps: StepSpec[] = stepKeys.map((key) => ({
    key,
    label:
      labelOverrides[key] ??
      DEFAULT_STEP_LABEL[key] ??
      titleCase(key),
    icon:
      iconOverrides[key] ??
      DEFAULT_STEP_ICON[key] ??
      "fallback",
  }));

  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="checkout-steps" data-testid="checkout-steps">
      {steps.map((step, index) => {
        const isActive = step.key === currentStep;
        const isCompleted = currentIndex > -1 && index < currentIndex;

        return (
          <div
            key={step.key}
            data-testid={`checkout-step-${step.key}`}
            data-step-state={isActive ? "active" : isCompleted ? "completed" : "upcoming"}
            className={`checkout-step ${
              isActive ? "active" : isCompleted ? "completed" : "upcoming"
            }`}
          >
            <div className="step-indicator">
              <span className="step-icon" aria-hidden="true">
                {isCompleted ? (
                  <CheckMark size={16} />
                ) : (
                  <Icon name={step.icon} size={18} />
                )}
              </span>
            </div>
            <div className="step-label">{step.label}</div>
            {index < steps.length - 1 && (
              <div
                className={`step-connector ${isCompleted ? "completed" : ""}`}
                aria-hidden="true"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CheckoutSteps;
